import React, { useState } from 'react';
import { MdAccountBalance, MdCheckCircle, MdArrowBack } from 'react-icons/md';

function backend() {
  return window.VEDA_API_URL || process.env.REACT_APP_BACKEND_URL || '';
}

async function getCsrf() {
  await fetch(`${backend()}/sanctum/csrf-cookie`, { credentials: 'include' });
  return decodeURIComponent(
    document.cookie.split('; ').find((c) => c.startsWith('XSRF-TOKEN='))?.split('=')[1] ?? ''
  );
}

async function inviaRichiesta(data) {
  const csrf = await getCsrf();
  const res = await fetch(`${backend()}/api/v1/pa/richieste-rup`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-XSRF-TOKEN': csrf },
    body: JSON.stringify(data),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(body.message || 'Errore durante l\'invio della richiesta.');
    err.errors = body.errors || {};
    throw err;
  }
  return body;
}

function Card({ children, maxWidth = 'max-w-md' }) {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <div className={`bg-white dark:bg-gray-800 rounded-2xl shadow-lg w-full ${maxWidth} p-8`}>{children}</div>
    </div>
  );
}

const TIPOLOGIE_ENTE = [
  '', 'Comune', 'Provincia', 'Regione', 'Città Metropolitana',
  'ASL', 'Università', 'Ente Pubblico Non Economico', 'Altro',
];

export default function RichiestaAccessoRupPage() {
  const [tipo,    setTipo]    = useState('ente'); // 'ente' | 'company'
  const [form,    setForm]    = useState({
    name: '', email: '', codice_fiscale: '', motivazione: '',
    ente_denominazione: '', ente_codice_ipa: '', ente_pec: '', ente_tipologia: '',
    company_ragione_sociale: '', company_partita_iva: '', company_codice_fiscale: '', company_pec: '',
  });
  const [errore,  setErrore]  = useState('');
  const [campi,   setCampi]   = useState({});
  const [loading, setLoading] = useState(false);
  const [inviata, setInviata] = useState(false);

  const isEnte    = tipo === 'ente';
  const isCompany = tipo === 'company';
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleInvia = async () => {
    setErrore('');
    setCampi({});

    // Validazione per campo: ogni errore compare sotto il relativo input,
    // solo per i campi della tipologia selezionata (ente o company).
    const errori = {};
    if (!form.name.trim())  errori.name  = ['Inserisci nome e cognome.'];
    if (!form.email.trim()) errori.email = ['Inserisci l\'email.'];
    if (form.motivazione.trim().length < 10) {
      errori.motivazione = ['La motivazione deve avere almeno 10 caratteri.'];
    }
    if (isEnte && !form.ente_denominazione.trim()) {
      errori.ente_denominazione = ['Indica la denominazione dell\'ente.'];
    }
    if (isCompany && !form.company_ragione_sociale.trim()) {
      errori.company_ragione_sociale = ['Indica la ragione sociale dell\'azienda.'];
    }
    if (Object.keys(errori).length > 0) {
      setCampi(errori);
      setErrore('Correggi i campi evidenziati prima di inviare la richiesta.');
      return;
    }
    setLoading(true);
    try {
      await inviaRichiesta({
        tipo,
        name: form.name,
        email: form.email,
        codice_fiscale: form.codice_fiscale || null,
        motivazione: form.motivazione,
        ente_denominazione: isEnte ? form.ente_denominazione : null,
        ente_codice_ipa:    isEnte ? (form.ente_codice_ipa || null) : null,
        ente_pec:           isEnte ? (form.ente_pec || null) : null,
        ente_tipologia:     isEnte ? (form.ente_tipologia || null) : null,
        company_ragione_sociale: isCompany ? form.company_ragione_sociale : null,
        company_partita_iva:     isCompany ? (form.company_partita_iva || null) : null,
        company_codice_fiscale:  isCompany ? (form.company_codice_fiscale || null) : null,
        company_pec:             isCompany ? (form.company_pec || null) : null,
      });
      setInviata(true);
    } catch (e) {
      setErrore(e.message);
      setCampi(e.errors || {});
    } finally {
      setLoading(false);
    }
  };

  if (inviata) {
    return (
      <Card>
        <div className="flex flex-col items-center gap-4 text-center">
          <MdCheckCircle className="text-5xl text-green-500" />
          <h1 className="text-xl font-bold text-gray-800 dark:text-white">Richiesta inviata</h1>
          <p className="text-gray-500 text-sm">
            La tua richiesta è stata inoltrata all'amministratore di sistema.
            Riceverai una notifica via email all'indirizzo <strong>{form.email}</strong> una
            volta che sarà stata valutata.
          </p>
          <a href="/login" className="mt-2 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium">
            <MdArrowBack /> Torna al login
          </a>
        </div>
      </Card>
    );
  }

  const fields = [
    { label: 'Nome e cognome', key: 'name', type: 'text', placeholder: 'Mario Rossi' },
    { label: 'Email', key: 'email', type: 'email', placeholder: 'nome@ente.it' },
    { label: 'Codice fiscale (opzionale)', key: 'codice_fiscale', type: 'text', placeholder: 'RSSMRA80A01H501U' },
  ];

  return (
    <Card maxWidth="max-w-2xl">
      <div className="flex flex-col items-center mb-6">
        <div className="w-16 h-16 rounded-2xl bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-300 mb-4">
          <MdAccountBalance size={32} />
        </div>
        <h1 className="text-xl font-bold text-gray-800 dark:text-white text-center">Richiesta di accesso</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center mt-1">
          Per enti pubblici e operatori economici. I RUP vengono invitati direttamente dall'ente.
        </p>
      </div>

      {/* Selettore tipo di accesso */}
      <div className="mb-5">
        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-2">Tipo di accesso</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {[
            { key: 'ente',    label: 'Ente pubblico', desc: 'Amministratore di un ente pubblico' },
            { key: 'company', label: 'Operatore Economico', desc: 'Azienda / promotore privato' },
          ].map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => { setTipo(opt.key); setErrore(''); setCampi({}); }}
              className={`text-left px-4 py-3 rounded-xl border-2 transition-colors ${
                tipo === opt.key
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-gray-600 hover:border-blue-300'
              }`}
            >
              <span className="block text-sm font-semibold text-gray-800 dark:text-white">{opt.label}</span>
              <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">{opt.desc}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-3 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {fields.map(({ label, key, type, placeholder }) => (
            <div key={key} className={`flex flex-col gap-1 ${key === 'codice_fiscale' ? 'sm:col-span-2' : ''}`}>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</label>
              <input
                type={type}
                value={form[key]}
                placeholder={placeholder}
                onChange={set(key)}
                className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-800 dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
              />
              {campi[key] && <span className="text-xs text-red-500">{campi[key][0]}</span>}
            </div>
          ))}
        </div>

        {/* Campi ente — solo per richieste di tipo ente */}
        {isEnte && (
          <div className="rounded-xl border border-gray-100 dark:border-gray-700 p-3 space-y-3 bg-gray-50/50 dark:bg-gray-700/20">
            <p className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">Dati dell'ente</p>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Denominazione ente</label>
              <input
                type="text"
                value={form.ente_denominazione}
                placeholder="Comune di Esempio"
                onChange={set('ente_denominazione')}
                className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-800 dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
              />
              {campi.ente_denominazione && <span className="text-xs text-red-500">{campi.ente_denominazione[0]}</span>}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Codice IPA</label>
                <input
                  type="text"
                  value={form.ente_codice_ipa}
                  placeholder="c_a123"
                  onChange={set('ente_codice_ipa')}
                  className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-800 dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
                />
                {campi.ente_codice_ipa && <span className="text-xs text-red-500">{campi.ente_codice_ipa[0]}</span>}
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Tipologia</label>
                <select
                  value={form.ente_tipologia}
                  onChange={set('ente_tipologia')}
                  className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-800 dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {TIPOLOGIE_ENTE.map((t) => <option key={t} value={t}>{t || 'Non specificata'}</option>)}
                </select>
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">PEC ente</label>
              <input
                type="email"
                value={form.ente_pec}
                placeholder="pec@ente.it"
                onChange={set('ente_pec')}
                className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-800 dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
              />
              {campi.ente_pec && <span className="text-xs text-red-500">{campi.ente_pec[0]}</span>}
            </div>
          </div>
        )}

        {/* Campi company — solo per richieste di tipo company */}
        {isCompany && (
          <div className="rounded-xl border border-gray-100 dark:border-gray-700 p-3 space-y-3 bg-gray-50/50 dark:bg-gray-700/20">
            <p className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">Dati dell'azienda</p>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Ragione sociale</label>
              <input
                type="text"
                value={form.company_ragione_sociale}
                placeholder="Acme S.r.l."
                onChange={set('company_ragione_sociale')}
                className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-800 dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
              />
              {campi.company_ragione_sociale && <span className="text-xs text-red-500">{campi.company_ragione_sociale[0]}</span>}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Partita IVA</label>
                <input
                  type="text"
                  value={form.company_partita_iva}
                  placeholder="12345678901"
                  onChange={set('company_partita_iva')}
                  className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-800 dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
                />
                {campi.company_partita_iva && <span className="text-xs text-red-500">{campi.company_partita_iva[0]}</span>}
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Codice fiscale</label>
                <input
                  type="text"
                  value={form.company_codice_fiscale}
                  placeholder="12345678901"
                  onChange={set('company_codice_fiscale')}
                  className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-800 dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
                />
                {campi.company_codice_fiscale && <span className="text-xs text-red-500">{campi.company_codice_fiscale[0]}</span>}
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">PEC azienda</label>
              <input
                type="email"
                value={form.company_pec}
                placeholder="pec@acme.it"
                onChange={set('company_pec')}
                className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-800 dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
              />
              {campi.company_pec && <span className="text-xs text-red-500">{campi.company_pec[0]}</span>}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Motivazione della richiesta</label>
          <textarea
            rows={4}
            value={form.motivazione}
            placeholder={
              isEnte ? "Descrivi l'ente e perché richiedi l'accesso come amministratore..."
                     : "Descrivi l'azienda e perché richiedi l'accesso come operatore economico..."}
            onChange={set('motivazione')}
            className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-800 dark:text-white outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          />
          {campi.motivazione && <span className="text-xs text-red-500">{campi.motivazione[0]}</span>}
        </div>
      </div>

      {errore && <p className="text-sm text-red-500 mb-3 text-center">{errore}</p>}

      <button
        onClick={handleInvia}
        disabled={loading}
        className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold transition-colors"
      >
        {loading
          ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Invio in corso...</>
          : 'Invia richiesta'}
      </button>

      <a href="/login" className="mt-4 flex items-center justify-center gap-1 text-sm text-gray-500 hover:text-blue-600">
        <MdArrowBack size={16} /> Torna al login
      </a>
    </Card>
  );
}
